import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import './LoginPage.css';
import './ForgotPasswordPage.css';
import logo from '../../assets/logo.png';
import hipaa from '../../assets/VitaRPM-HIPAA.png';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:3000/api').replace(/\/$/, '');

/** Rejects garbage like }}%}#$%}#@ x.com; allows normal addresses only */
function getRealTimeEmailError(raw) {
    if (raw == null || !String(raw).trim()) return null;
    const t = String(raw).trim();
    if (t.length > 254) return 'Invalid email format.';
    const re =
        /^[a-zA-Z0-9](?:[a-zA-Z0-9._+~-]*[a-zA-Z0-9])?@[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?)+$/;
    if (!re.test(t)) return 'Invalid email format.';
    return null;
}

const ForgotPasswordPage = () => {
    const [email, setEmail] = useState('');
    const [showLoading, setShowLoading] = useState(false);
    const [localError, setLocalError] = useState('');
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const navigate = useNavigate();

    const emailFormatError = useMemo(() => getRealTimeEmailError(email), [email]);
    const submitDisabled = showLoading || (String(email).trim() !== '' && emailFormatError);

    const handleChange = (e) => {
        setEmail(e.target.value);
        if (localError) {
            setLocalError('');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        setLocalError('');

        if (!email || email.trim() === '') {
            setLocalError('Email is required.');
            return;
        }

        if (getRealTimeEmailError(email)) {
            return;
        }

        setShowLoading(true);

        try {
            const response = await fetch(`${API_BASE}/users/forgot-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({ email: email.trim() }),
            });

            const data = await response.json();

            if (!response.ok) {
                setLocalError(data.message || `Request failed (HTTP ${response.status})`);
                return;
            }

            if (!data.success) {
                setLocalError(data.message || 'Request failed');
                return;
            }

            setShowSuccessModal(true);
        } catch (error) {
            console.error('Forgot password error:', error);
            setLocalError('An error occurred. Please try again.');
        } finally {
            setShowLoading(false);
        }
    };

    const handleCloseModal = () => {
        setShowSuccessModal(false);
    };

    const handleModalBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            handleCloseModal();
        }
    };

    return (
        <>
            {showSuccessModal && (
                <div
                    className="success-modal-overlay"
                    onClick={handleModalBackdropClick}
                >
                    <div
                        className="success-modal-content"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="success-modal-header">
                            <h4>Password Reset Email Sent</h4>
                        </div>

                        <div className="success-modal-body">

                            <div className="success-icon-container">
                                <i className="fa fa-check-circle"></i>
                            </div>

                            <p>
                                If an account exists with the email address you
                                entered, a password reset link has been sent.
                            </p>

                            <p className="small-text">
                                Please check your inbox and spam folder.
                            </p>

                        </div>

                        <div className="success-modal-footer">
                            <button
                                type="button"
                                className="vrpm-login-btn"
                                onClick={handleCloseModal}
                            >
                                Continue
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="vrpm-login-page">

                {/* LEFT PANEL */}

                <div className="vrpm-login-left">

                    <div className="vrpm-logo">
                        <img src={logo} alt="VitaRPM" />
                    </div>

                    <div className="vrpm-form-wrapper">

                        <div
                            className="vrpm-back-link"
                            onClick={() => navigate("/login")}
                        >
                            <i className="fa fa-arrow-left"></i>
                            <span>Back to Sign In</span>
                        </div>

                        <h1>Reset Your Password</h1>

                        <p className="vrpm-subtitle">
                            Enter the email address associated with your VitaRPM
                            account and we'll send you a secure password reset link.
                        </p>

                        <form
                            onSubmit={handleSubmit}
                            noValidate
                            autoComplete="off"
                        >

                            <div className="vrpm-field">

                                <label>Email Address</label>

                                <input
                                    id="email"
                                    type="text"
                                    name="email"
                                    inputMode="email"
                                    value={email}
                                    onChange={handleChange}
                                    placeholder="Enter your registered email address"
                                    autoComplete="email"
                                    autoFocus
                                    className={
                                        emailFormatError
                                            ? "vrpm-invalid"
                                            : ""
                                    }
                                />

                                {emailFormatError && (
                                    <div className="vrpm-error mt-2">
                                        {emailFormatError}
                                    </div>
                                )}

                            </div>

                            {localError && (
                                <div className="vrpm-error">
                                    {localError}
                                </div>
                            )}

                            <button
                                type="submit"
                                className="vrpm-login-btn"
                                disabled={submitDisabled}
                            >
                                {showLoading ? (
                                    <>
                                        <i
                                            className="fa fa-spinner fa-spin"
                                            style={{ marginRight: 10 }}
                                        ></i>
                                        Sending...
                                    </>
                                ) : (
                                    "Send Reset Link"
                                )}
                            </button>

                        </form>

                    </div>

                </div>

                {/* RIGHT PANEL */}

                <div className="vrpm-login-right-container">
                    <div className="vrpm-login-right">
                        <div className="vrpm-right-overlay">

                            <div className="vrpm-hero">

                                <h2>
                                    Revolutionize Patient Care <br></br>with VitaRPM
                                </h2>

                                <p className="vrpm-quote">
                                    “With VitaRPM, our clinicians stay connected to patients beyond the clinic.
                                    The platform is intuitive, secure, and provides the visibility we need to intervene early
                                    and improve patient outcomes.”
                                </p>

                                <div className="vrpm-testimonial">

                                    <div className="vrpm-avatar">

                                        <img
                                            src="https://randomuser.me/api/portraits/women/71.jpg"
                                            alt=""
                                        />

                                    </div>

                                    <div>

                                        <h4>Isabela Torres, MD</h4>

                                        <span>
                                            Director Digital Health
                                        </span>

                                    </div>

                                </div>

                            </div>

                            <div className="vrpm-bottom">

                                <div className="vrpm-divider"></div>
                                <div className="vrpm-hipaa">
                                    <img src={hipaa} alt="VitaRPM" />
                                </div>
                                <div className="vrpm-cpt-grid">

                                    <div>CPT 99453</div>
                                    <div>CPT 99445</div>
                                    <div>CPT 99454</div>
                                    <div>CPT 99470</div>
                                    <div>CPT 99457</div>
                                    <div>CPT 99458</div>

                                </div>

                            </div>

                        </div>
                    </div>
                </div>

            </div>

        </>
    );
};

export default ForgotPasswordPage;
